import { useState, useEffect, useCallback } from 'react';

export const useMicrophone = () => {
  const [microphones, setMicrophones] = useState([]);
  const [selectedMic, setSelectedMic] = useState(null);
  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);

  const getMicrophones = useCallback(async () => {
    try {
      // Request permission first
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionGranted(true);
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      
      setMicrophones(audioInputs);
      
      // Set default microphone if not already set
      if (audioInputs.length > 0 && !selectedMic) {
        const defaultMic = audioInputs.find(mic => mic.deviceId === 'default') || audioInputs[0];
        setSelectedMic(defaultMic.deviceId);
      }
      
      setError(null);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied');
        setPermissionGranted(false);
      } else {
        setError('Could not access microphones');
      }
    }
  }, [selectedMic]);

  useEffect(() => {
    getMicrophones();
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', getMicrophones);
    
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getMicrophones);
    };
  }, [getMicrophones]);

  const selectMicrophone = useCallback((deviceId) => {
    setSelectedMic(deviceId);
    localStorage.setItem('selected_microphone', deviceId);
  }, []);

  // Load saved microphone preference
  useEffect(() => {
    const saved = localStorage.getItem('selected_microphone');
    if (saved && microphones.some(mic => mic.deviceId === saved)) {
      setSelectedMic(saved);
    }
  }, [microphones]);

  return {
    microphones,
    selectedMic,
    selectMicrophone,
    error,
    permissionGranted,
    refreshMicrophones: getMicrophones,
  };
};
